/**
 * 동료 합류 장면. 전투 결과로 가기 전에 한 화면을 통째로 쓴다.
 *
 * 합류는 이 게임에서 드문 일이다(깊이 2·4·6을 처음 넘을 때 한 명씩). 예전에는 결과
 * 화면의 판 하나로 지나갔고, 플레이한 사람이 "장수를 얻는 이벤트 씬"을 달라고 했다.
 * 누구인지, 무엇을 했던 사람인지, 부대에 무엇을 주는지, 그리고 이제 부하 부대가
 * 하나 열린다는 것을 여기서 알린다 — 부하 부대는 동료를 맡겨야 열린다.
 */
import { MATE_SKILL_BY_ID, mateSkillText } from '../sim/mate-skills.ts';
import type { CompanionDef } from '../sim/troops.ts';
import { CULTURE_NAMES } from '../sim/troops.ts';
import { drawCompanionPortrait } from '../render/atlas.ts';
import { companionSpec } from '../sim/troops.ts';
import { drawUnitPortrait, iconKeyFor } from './unit-icon.ts';
import { ARCHETYPE_LINE, COMPANION_LORE } from './companion-lore.ts';
import { button, clear, el, subjectJosa } from './dom.ts';

export function renderJoinedScene(root: HTMLElement, def: CompanionDef, onDone: () => void): void {
  clear(root);
  const lore = COMPANION_LORE[def.id];
  const portrait = document.createElement('canvas');
  portrait.className = 'joined-portrait';
  // 그림은 꾸밈이다. 누구인지는 아래 글이 말한다.
  portrait.setAttribute('aria-hidden', 'true');
  // 그 동료의 그림(걸어서 오는 모습). 그림을 받기 전과 받지 못했을 때는 예전 코드 그림을 둔다.
  drawCompanionPortrait(portrait, def.culture);
  // 걸어서 오는 모습(착장 없이).
  const key = iconKeyFor(companionSpec(def));
  if (key) void drawUnitPortrait(portrait, key);
  const skill = MATE_SKILL_BY_ID.get(def.id);
  const line = ARCHETYPE_LINE[def.title];

  const done = button('맞이한다', onDone, 'primary');
  // 포커스는 곧장 버튼으로 간다. 그래서 스크린리더가 버튼만 읽고 누구를 맞이하는지는
  // 건너뛰었다(리뷰가 찾았다). 장면을 대화상자로 두고 이름과 글을 이어 준다.
  const described = lore ? 'joined-who joined-story joined-gift' : 'joined-who joined-gift';
  root.append(
    el(
      'div',
      {
        // 합류 장면은 늘 어두운 무대다. 메뉴가 라이트여도 어두운 토큰을 다시 건다(ui/theme.ts).
        class: 'joined-scene theme-dark',
        role: 'dialog',
        'aria-modal': 'true',
        'aria-labelledby': 'joined-name',
        'aria-describedby': described,
      },
      el(
        'div',
        { class: 'joined-card' },
        el('span', { class: 'eyebrow' }, '새 부하장수'),
        portrait,
        el('h1', { class: 'joined-name', id: 'joined-name' }, def.name),
        el(
          'div',
          { class: 'joined-who', id: 'joined-who' },
          `${CULTURE_NAMES[def.culture]} · ${lore?.who ?? def.title}`,
        ),
        lore ? el('p', { class: 'joined-story', id: 'joined-story' }, lore.story) : null,
        line ? el('p', { class: 'joined-line' }, `“${line}”`) : null,
        el(
          'div',
          { class: 'joined-gift', id: 'joined-gift' },
          el('b', {}, def.title),
          el('span', {}, def.note),
          // 고유 기술(Todo10) — 만나는 자리에서 무엇을 하는 사람인지 알린다.
          skill ? el('span', {}, `고유 기술 「${skill.name}」 — ${mateSkillText(skill, 1)}`) : null,
        ),
        el(
          'p',
          { class: 'dim' },
          `${def.name}${subjectJosa(def.name)} 부하 부대 하나를 맡는다. 출정 화면에서 병사를 나눠 주면 전장에서 명령을 따로 받는다. ` +
            '말이 없으면 걸어서 싸운다 — 장비에서 말을 채워 주면 말에 오른다.',
        ),
        done,
      ),
    ),
  );
  done.focus();
}
